import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { PayrollPayment, PayrollReport } from '@gigdriver/domain';
import { payrollToCsv } from '@gigdriver/domain';
import {
  finalizePayrollReport,
  generatePayrollReport,
  getPayrollPdfUrl,
  getPaymentStatementUrl,
  markPayrollPaid,
  recordPersonPayment,
  reopenPayrollReport,
  watchGigPayrollReport,
  watchOrgPayrollPayments,
  watchOrgPayrollReports,
} from '@gigdriver/client';

// Payroll reports: the open gig's live report, the org-wide list, and the
// actions. Everything that writes is a Cloud Function (manager+).
export const usePayrollStore = defineStore('payroll', () => {
  const report = ref<PayrollReport | null>(null);
  const reports = ref<PayrollReport[]>([]);
  const payments = ref<PayrollPayment[]>([]);
  const busy = ref(false);
  const error = ref<string | null>(null);

  let stopGig: (() => void) | null = null;
  let stopOrg: (() => void) | null = null;
  let stopPayments: (() => void) | null = null;

  const fail = (ctx: string) => (err: unknown) => {
    error.value = err instanceof Error ? err.message : ctx;
  };

  function watchGig(orgId: string, gigId: string) {
    stopGig?.();
    report.value = null;
    stopGig = watchGigPayrollReport(orgId, gigId, r => (report.value = r), fail('Failed to load payroll'));
  }

  function watchOrg(orgId: string) {
    stopOrg?.();
    reports.value = [];
    stopOrg = watchOrgPayrollReports(orgId, r => (reports.value = r), fail('Failed to load payroll reports'));
    stopPayments?.();
    payments.value = [];
    stopPayments = watchOrgPayrollPayments(orgId, p => (payments.value = p), fail('Failed to load payments'));
  }

  // Each page stops ONLY the watcher it started. A shared "stop everything"
  // is a trap: when navigating between pages the incoming page mounts before
  // the outgoing one unmounts, so the old page's cleanup would kill the new
  // page's live subscription.
  function stopGigWatch() {
    stopGig?.();
    stopGig = null;
  }
  function stopOrgWatch() {
    stopOrg?.();
    stopOrg = null;
    stopPayments?.();
    stopPayments = null;
  }

  // Runs a write, tracking busy/error; rethrows so the caller can react.
  async function run<T>(fn: () => Promise<T>, ctx: string): Promise<T> {
    busy.value = true;
    error.value = null;
    try {
      return await fn();
    } catch (err) {
      fail(ctx)(err);
      throw err;
    } finally {
      busy.value = false;
    }
  }

  // Saves a Blob/URL as a file. A data: or signed URL is fetched through an
  // anchor with `download` so it works for the emulator's data: URLs too.
  function saveAs(href: string, filename: string) {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function downloadCsv(r: PayrollReport, gigName: string) {
    const blob = new Blob([payrollToCsv(r)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    saveAs(url, `payroll-${gigName.replace(/[^\w.-]+/g, '_')}-v${r.version}.csv`);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  return {
    report,
    reports,
    payments,
    busy,
    error,
    watchGig,
    watchOrg,
    stopGigWatch,
    stopOrgWatch,
    downloadCsv,
    generate: (orgId: string, gigId: string) => run(() => generatePayrollReport({ orgId, gigId }), 'Failed to generate payroll'),
    finalize: (orgId: string, gigId: string) => run(() => finalizePayrollReport({ orgId, gigId }), 'Failed to finalize payroll'),
    markPaid: (orgId: string, gigId: string, paidAt: Date | undefined, reference: string | undefined) =>
      run(() => markPayrollPaid({ orgId, gigId, paidAt, reference }), 'Failed to mark payroll paid'),
    reopen: (orgId: string, gigId: string, reason: string) =>
      run(() => reopenPayrollReport({ orgId, gigId, reason }), 'Failed to reopen payroll'),
    // Records a payment to one person for the chosen gigs, then downloads the
    // statement. The payment is committed once recordPersonPayment returns; if
    // only the download then fails, the error says so and the statement can be
    // fetched again from the person's payment history.
    payPerson: async (orgId: string, personId: string, gigIds: string[], paidAt: Date | undefined, reference: string | undefined) => {
      const result = await run(() => recordPersonPayment({ orgId, personId, gigIds, paidAt, reference }), 'Failed to record payment');
      try {
        await run(async () => {
          const { url } = await getPaymentStatementUrl({ orgId, paymentId: result.paymentId });
          saveAs(url, `payment-statement-${result.name.replace(/[^\w.-]+/g, '_')}-${result.paymentId.slice(0, 6)}.pdf`);
        }, 'Payment recorded, but the statement could not be downloaded — download it from payment history.');
      } catch {
        // surfaced via store.error; the payment itself succeeded
      }
      return result;
    },
    downloadStatement: (orgId: string, paymentId: string, name: string) =>
      run(async () => {
        const { url } = await getPaymentStatementUrl({ orgId, paymentId });
        saveAs(url, `payment-statement-${name.replace(/[^\w.-]+/g, '_')}-${paymentId.slice(0, 6)}.pdf`);
      }, 'Failed to download the statement'),
    downloadPdf: (orgId: string, gigId: string, gigName: string, version: number) =>
      run(async () => {
        const { url } = await getPayrollPdfUrl({ orgId, gigId });
        saveAs(url, `payroll-${gigName.replace(/[^\w.-]+/g, '_')}-v${version}.pdf`);
      }, 'Failed to download the PDF'),
  };
});
